import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { EditorContent } from '@tiptap/react';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './DescriptionEditor.module.scss';
import FormatChrome from './components/FormatChrome';
import MentionList from './components/MentionList';
import useTipTapEditor from './hooks/useTipTapEditor';
import useUiMotion from './hooks/useUiMotion';
import { getMentionedHandles } from './utils/editorHelpers';

/**
 * Rich-text editor surface (TipTap) with @-mention suggestions, inline
 * formatting controls, and a link panel. Orchestrates co-located hooks +
 * subcomponents:
 *
 *   hooks/useTipTapEditor     — extension config + onCreate/onUpdate/etc.
 *   hooks/useUiMotion         — shared motion config (reduced-motion aware)
 *   hooks/useLinkMode         — owned by <FormatChrome>
 *   hooks/useEditorChromeSync — owned by <FormatChrome>
 *   components/FormatChrome   — Aa + link toolbar (dock / fixed / inline)
 *   components/LinkPanel      — URL / text inputs + Apply/Cancel (owned by
 *                               FormatChrome)
 *   components/MentionList    — portaled @-mention dropdown
 *   components/Icons          — LinkIcon / TextLinesIcon SVGs
 *   utils/editorHelpers       — TipTap helpers + mentions query
 *
 * The parent gets a stable imperative handle (`getHTML`, `setContent`,
 * `clear`, etc.) so it doesn't need to reach into TipTap internals.
 */
const DescriptionEditor = forwardRef(
  (
    {
      initialContent = '',
      onChange,
      placeholder: placeholderText = "What's on your mind?",
      maxLength = 3000,
      onFocus,
      onBlur,
      onKeyDown,
      /** `focus`: show Aa / link / format strip only while the editor or link dialog needs it (e.g. comments). */
      formatChromeVisibility = 'always',
      /** `comment`: tighter typography + aligned placeholder (matches ProseMirror). */
      variant = 'default',
    },
    ref
  ) => {
    const mentionListRef = useRef(null);
    const [mentionProps, setMentionProps] = useState(null);
    const mentionActiveRef = useRef(false);
    /** Set to 'space' when Space ends the @-mention query (so we can clear stored marks). */
    const mentionDismissReasonRef = useRef(null);

    const isMobile =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const [mobileToolbarVisible, setMobileToolbarVisible] = useState(false);
    const blurTimerRef = useRef(null);
    const cancelBlur = useCallback(
      () => clearTimeout(blurTimerRef.current),
      []
    );
    const [editorFocused, setEditorFocused] = useState(false);
    const [linkModeOpen, setLinkModeOpen] = useState(false);
    const uiMotion = useUiMotion();

    // TipTap's @tiptap/extension-placeholder uses isNodeEmpty(), which is
    // false for a paragraph containing only ProseMirror's trailing
    // hardBreak — so the official placeholder often never renders. We show
    // copy when character count is 0 instead.
    const [showPlaceholderOverlay, setShowPlaceholderOverlay] = useState(true);

    const editor = useTipTapEditor({
      initialContent,
      placeholderText,
      maxLength,
      isMobile,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      mentionActiveRef,
      mentionDismissReasonRef,
      mentionListRef,
      setMentionProps,
      setShowPlaceholderOverlay,
      setEditorFocused,
      setMobileToolbarVisible,
      blurTimerRef,
    });

    const showDockChrome =
      editor &&
      !isMobile &&
      (formatChromeVisibility === 'always' || editorFocused || linkModeOpen);

    useEffect(() => {
      if (!showDockChrome) setLinkModeOpen(false);
    }, [showDockChrome]);

    useImperativeHandle(
      ref,
      () => ({
        getHTML: () => editor?.getHTML() ?? '',
        getMentionedHandles: () =>
          editor ? getMentionedHandles(editor) : [],
        setContent: (html) => editor?.commands.setContent(html ?? '', false),
        focus: () => editor?.commands.focus(),
        clear: () => editor?.commands.clearContent(true),
        getCharacterCount: () =>
          editor?.storage.characterCount.characters() ?? 0,
      }),
      [editor]
    );

    const handleMentionSelect = (user) => {
      mentionProps?.command({ id: user.handle, label: user.handle });
    };

    return (
      <div
        className={`${styles.container} ${
          variant === 'comment' ? styles.commentVariant : ''
        } ${isMobile ? styles.mobileGrow : ''}`}
      >
        <div className={styles.editorSurface}>
          {showPlaceholderOverlay && (
            <div className={styles.placeholderOverlay} aria-hidden="true">
              {placeholderText}
            </div>
          )}
          <EditorContent editor={editor} className={styles.editorContent} />
        </div>
        {variant === 'comment' ? (
          <AnimatePresence>
            {showDockChrome && (
              <motion.div
                key="format-dock"
                className={styles.dockChromeMotionWrap}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  y: 6,
                  transition: {
                    ...uiMotion.pop,
                    delay: uiMotion.reduced ? 0 : 0.07,
                  },
                }}
                transition={uiMotion.pop}
              >
                <FormatChrome
                  editor={editor}
                  layout="dock"
                  onLinkModeChange={setLinkModeOpen}
                />
              </motion.div>
            )}
          </AnimatePresence>
        ) : (
          showDockChrome && (
            <FormatChrome
              editor={editor}
              layout="dock"
              onLinkModeChange={setLinkModeOpen}
            />
          )
        )}
        <AnimatePresence>
          {editor && isMobile && mobileToolbarVisible && (
            <FormatChrome
              key="format-chrome-mobile"
              editor={editor}
              cancelBlur={cancelBlur}
              layout="inline"
              onLinkModeChange={setLinkModeOpen}
            />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {mentionProps && (
            <MentionList
              key="mention-dropdown"
              ref={mentionListRef}
              items={mentionProps.items}
              query={mentionProps.query}
              clientRect={mentionProps.clientRect}
              onSelect={handleMentionSelect}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
);

DescriptionEditor.displayName = 'DescriptionEditor';
export default DescriptionEditor;
